/**
 * Normalise une chaîne de caractères pour la recherche
 * - Retire les accents
 * - Convertit en minuscules
 * - Retire les caractères spéciaux
 */
export const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Retire les accents
    .replace(/[^a-z0-9\s]/g, '') // Retire les caractères spéciaux
    .trim();
};

/**
 * Calcule la distance de Levenshtein entre deux chaînes
 * Utilisé pour détecter les fautes de frappe
 */
export const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix: number[][] = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // Substitution
          matrix[i][j - 1] + 1,     // Insertion
          matrix[i - 1][j] + 1      // Suppression
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
};

/**
 * Vérifie si une chaîne correspond à la recherche avec tolérance aux fautes
 * @param text - Texte à analyser
 * @param searchTerm - Terme de recherche
 * @param tolerance - Nombre de fautes tolérées (par défaut 2)
 * @returns true si le texte correspond à la recherche
 */
export const fuzzyMatch = (
  text: string,
  searchTerm: string,
  tolerance: number = 2
): boolean => {
  const normalizedText = normalizeString(text);
  const normalizedSearch = normalizeString(searchTerm);

  // Correspondance exacte
  if (normalizedText.includes(normalizedSearch)) {
    return true;
  }

  // Recherche par mots
  const textWords = normalizedText.split(/\s+/);
  const searchWords = normalizedSearch.split(/\s+/);

  // Vérifier si chaque mot de recherche correspond à un mot du texte
  return searchWords.every(searchWord => {
    return textWords.some(textWord => {
      // Correspondance exacte du mot
      if (textWord.includes(searchWord)) {
        return true;
      }

      // Tolérance aux fautes si les mots sont assez longs
      if (searchWord.length >= 4 && textWord.length >= 4) {
        const distance = levenshteinDistance(searchWord, textWord);
        const maxLength = Math.max(searchWord.length, textWord.length);
        
        // Accepter si le ratio de similarité est suffisant
        return distance <= tolerance && (distance / maxLength) <= 0.4;
      }

      return false;
    });
  });
};

/**
 * Le nom saisi par un médecin ("cardio", "urgences", "médecine générale") ne
 * correspond pas au nom enregistré en base ("Cardiologue", "Urgentiste",
 * "Généraliste"). Cette correspondance tolère les débuts de mot, les pluriels
 * et les variations de suffixe (-ie / -eur / -iste / -atre) pour ne jamais
 * afficher « 0 mission » alors que des missions existent.
 */
const WORD_SUFFIXES = [
  "ements", "ement", "ations", "ation", "ions", "ion",
  "istes", "iste", "eurs", "eur", "ies", "ie",
  "aux", "ales", "ale", "es", "s", "e",
];

// Découpe en mots en gardant les apostrophes et tirets comme séparateurs
const wordsOf = (str: string): string[] =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .split(/[^a-z]+/)
    .filter((word) => word.length >= 3);

const rootWord = (word: string): string => {
  for (const suffix of WORD_SUFFIXES) {
    if (word.endsWith(suffix) && word.length - suffix.length >= 4) {
      return word.slice(0, -suffix.length);
    }
  }
  return word;
};

const commonPrefixLength = (a: string, b: string): number => {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return i;
};

const wordMatches = (searchWord: string, valueWord: string): boolean => {
  if (valueWord.includes(searchWord) || searchWord.includes(valueWord)) return true;

  const searchRoot = rootWord(searchWord);
  const valueRoot = rootWord(valueWord);

  if (searchRoot.length >= 4 && (valueWord.startsWith(searchRoot) || valueRoot.startsWith(searchRoot))) {
    return true;
  }
  if (valueRoot.length >= 4 && searchWord.startsWith(valueRoot)) return true;
  if (commonPrefixLength(searchWord, valueWord) >= 6) return true;

  // "urgences" / "urgentiste" : racines presque identiques, une lettre divergente
  if (searchRoot.length >= 4 && valueRoot.length >= 4 && commonPrefixLength(searchRoot, valueRoot) >= 5) {
    const distance = levenshteinDistance(searchRoot, valueRoot);
    const maxLength = Math.max(searchRoot.length, valueRoot.length);
    return distance <= 2 && distance / maxLength <= 0.4;
  }

  return false;
};

/**
 * Vérifie qu'une spécialité saisie par l'utilisateur désigne la spécialité
 * d'une offre, même en cas d'approximation.
 */
export const specialtyMatches = (specialtyValue: string, query: string): boolean => {
  const searchWords = wordsOf(query);
  if (searchWords.length === 0) return true;
  const valueWords = wordsOf(specialtyValue);
  if (valueWords.length === 0) return false;

  return searchWords.some((searchWord) =>
    valueWords.some((valueWord) => wordMatches(searchWord, valueWord))
  );
};

/**
 * Filtre une liste d'éléments avec recherche floue
 * @param items - Liste d'éléments à filtrer
 * @param searchTerm - Terme de recherche
 * @param fields - Champs à rechercher dans chaque élément
 * @returns Liste filtrée
 */
export const fuzzySearch = <T extends Record<string, any>>(
  items: T[],
  searchTerm: string,
  fields: (keyof T)[]
): T[] => {
  if (!searchTerm.trim()) {
    return items;
  }

  return items.filter(item => {
    return fields.some(field => {
      const value = item[field];
      if (typeof value === 'string') {
        return fuzzyMatch(value, searchTerm);
      }
      return false;
    });
  });
};
